/**
 * Client for the DeckorateAPI Create-Slides endpoint — AI design / freehand
 * (LLM call), SSE streaming.
 *   POST /v1/deckorate/slides/create-slide-ai  (application/json) → text/event-stream
 *
 * DeckorateAPI is a SEPARATE FastAPI service (local dev: port 8002, run via
 * `make dev-deckorate`), not the main ai-api (8000). It has no `/api` prefix.
 * The base URL comes from config.deckorateApiBaseUrl; in local dev that's empty
 * and the Vite proxy forwards `/v1/slides/*` to localhost:8002.
 *
 * Document input is via blob storage (blob_container/blob_name) — the backend
 * extracts text server-side. (The "use slide templates" path is a separate
 * endpoint/payload — TBD.)
 *
 * Keep this file the single source of truth for the wire shapes so the rest of
 * the feature stays decoupled from the contract.
 *
 * Auth is not handled here: callers pass the shared `AuthorizedFetch` from
 * `useDeckorateHttp()` (bearer + APIM key + 401 refresh + telemetry), so this
 * client only adds the request-shape headers (Accept / Content-Type).
 */

import type { AuthorizedFetch } from "../../../services/deckorateApi";

const CREATE_SLIDE_PATH = "/v1/deckorate/slides/create-slide-ai";

/* ── Shared value types (models/common.py) ──────── */

export type ContractTone =
  | "professional"
  | "casual"
  | "persuasive"
  | "educational"
  | "inspirational";

export type IntentTag =
  | "cover"
  | "section"
  | "body"
  | "list"
  | "comparison"
  | "swot"
  | "process"
  | "timeline"
  | "hierarchical"
  | "statistical"
  | "agenda"
  | "closing";

export interface SlideDimensions {
  cx: number;
  cy: number;
}

export interface ThemeColors {
  dk1?: string;
  lt1?: string;
  dk2?: string;
  lt2?: string;
  accent1?: string;
  accent2?: string;
  accent3?: string;
  accent4?: string;
  accent5?: string;
  accent6?: string;
  hlink?: string;
}

/* ── Request (POST /v1/slides/generate) ─────────── */

export interface GeneratePresentationRequest {
  request_id?: string; // server fills if omitted
  input_type: "topic" | "document";
  topic?: string; // max 4000; required (>= 3 chars) for input_type="topic"
  /** Blob storage location for document input (backend extracts server-side). */
  blob_container?: string; // falls back to AZURE_DEFAULT_CONTAINER
  blob_name?: string; // required when input_type="document"
  audience?: string; // max 400
  slide_count: number; // 3..30
  tone: ContractTone;
  include_cover: boolean;
  include_closing: boolean;
  slide_dimensions?: SlideDimensions;
  theme_colors?: ThemeColors;
}

/* ── SSE payload shapes ─────────────────────────── */

export interface SlideBrief {
  slide_idx: number;
  title: string;
  subtitle?: string | null;
  intent_tag: IntentTag;
  main_points: string[];
}

export interface OutlinePayload {
  request_id: string;
  title: string;
  slide_count: number;
  slides: SlideBrief[];
}

export type SlideStatus = "ok" | "failed";

export interface SlideXmlResult {
  slide_idx: number;
  title: string;
  intent_tag: IntentTag;
  status: SlideStatus;
  xml?: string | null;
  /**
   * Directly-insertable base64 `.pptx` for this slide — the preferred render
   * path (FE inserts via Presentation.insertSlidesFromBase64). Field name isn't
   * finalised; `pptx` is accepted as an alias. When absent, the FE falls back
   * to a placeholder slide built from the outline points.
   */
  pptx_base64?: string | null;
  pptx?: string | null;
  valid?: boolean;
  attempts?: number;
  error?: string | null;
  code?: string;
  http_status?: number;
}

/** Extract the base64 `.pptx` from a slide result, whatever the field is called. */
export function slidePptxBase64(result: SlideXmlResult): string | undefined {
  return result.pptx_base64 ?? result.pptx ?? undefined;
}

export interface GenerationSummary {
  request_id: string;
  total: number;
  completed: number;
  failed: number;
  slides: SlideXmlResult[];
  meta?: { request_id: string; model_id: string; source: string; processing_ms?: number };
}

/** Error raised for HTTP-level failures (before the 200 stream). */
export class SlideApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly httpStatus?: number,
  ) {
    super(message);
    this.name = "SlideApiError";
  }
}

/* ── Event callbacks ────────────────────────────── */

export interface SlideStreamEvents {
  onStart?: (data: { request_id: string; slide_count: number; source: string; mode: string }) => void;
  onOutline?: (data: OutlinePayload) => void;
  onSlideStart?: (data: { request_id: string; slide_idx: number; title: string; intent_tag: IntentTag }) => void;
  onSlideXml?: (data: { request_id: string; slide_idx: number; text: string }) => void;
  onStatus?: (data: { request_id: string; phase: string; message: string }) => void;
  onSlideComplete?: (data: SlideXmlResult) => void;
  onDone?: (data: GenerationSummary) => void;
  /** SSE-level fatal error (HTTP already 200). */
  onError?: (data: { request_id?: string; code: string; message: string }) => void;
}

export interface StreamOptions {
  /** DeckorateAPI base URL (config.deckorateApiBaseUrl). */
  baseUrl: string;
  /** Shared authorised fetch (bearer + APIM key + 401 refresh) from useDeckorateHttp(). */
  authorizedFetch: AuthorizedFetch;
  signal?: AbortSignal;
}

const DEFAULT_DIMENSIONS: SlideDimensions = { cx: 12192000, cy: 6858000 };

/** Throws SlideApiError for a non-200 response, parsing the ErrorResponse envelope. */
async function throwForHttpError(res: Response): Promise<never> {
  let code = `HTTP_${res.status}`;
  let message = `Request failed (${res.status})`;
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } };
    if (body.error?.code) code = body.error.code;
    if (body.error?.message) message = body.error.message;
  } catch {
    /* non-JSON error body */
  }
  console.error(`[create-slide] request failed (${res.status} ${code}):`, message);
  throw new SlideApiError(code, message, res.status);
}

/** Dispatch one parsed SSE frame to the right callback. */
function dispatch(event: string, data: unknown, events: SlideStreamEvents): void {
  switch (event) {
    case "start":
      events.onStart?.(data as Parameters<NonNullable<SlideStreamEvents["onStart"]>>[0]);
      break;
    case "outline":
      events.onOutline?.(data as OutlinePayload);
      break;
    case "slide_start":
      events.onSlideStart?.(data as Parameters<NonNullable<SlideStreamEvents["onSlideStart"]>>[0]);
      break;
    case "slide_xml":
      events.onSlideXml?.(data as Parameters<NonNullable<SlideStreamEvents["onSlideXml"]>>[0]);
      break;
    case "status":
      events.onStatus?.(data as Parameters<NonNullable<SlideStreamEvents["onStatus"]>>[0]);
      break;
    case "slide_complete":
      events.onSlideComplete?.(data as SlideXmlResult);
      break;
    case "done":
      events.onDone?.(data as GenerationSummary);
      break;
    case "error":
      events.onError?.(data as Parameters<NonNullable<SlideStreamEvents["onError"]>>[0]);
      break;
    default:
      break;
  }
}

/** Read an SSE body stream and dispatch each `event:`/`data:` frame. */
async function consumeSse(res: Response, events: SlideStreamEvents): Promise<void> {
  if (!res.body) throw new SlideApiError("NO_STREAM", "Response had no body stream");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const handleFrame = (frame: string) => {
    let eventName = "message";
    const dataLines: string[] = [];
    for (const line of frame.split("\n")) {
      if (line.startsWith("event:")) eventName = line.slice(6).trim();
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
    }
    if (dataLines.length === 0) return;
    try {
      dispatch(eventName, JSON.parse(dataLines.join("\n")), events);
    } catch (e) {
      console.warn(`[create-slide] skipped malformed SSE frame (event: ${eventName}):`, e);
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep: number;
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      handleFrame(buffer.slice(0, sep));
      buffer = buffer.slice(sep + 2);
    }
  }
  if (buffer.trim()) handleFrame(buffer);
}

/** POST /v1/slides/generate — JSON body, SSE response. */
export async function streamSlideGeneration(
  req: GeneratePresentationRequest,
  events: SlideStreamEvents,
  opts: StreamOptions,
): Promise<void> {
  const res = await opts.authorizedFetch(`${opts.baseUrl.replace(/\/$/, "")}${CREATE_SLIDE_PATH}`, {
    method: "POST",
    headers: { Accept: "text/event-stream", "Content-Type": "application/json" },
    body: JSON.stringify({ slide_dimensions: DEFAULT_DIMENSIONS, ...req }),
    signal: opts.signal,
  });
  if (!res.ok) await throwForHttpError(res);
  await consumeSse(res, events);
}
